/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description MultiSelect component
 */
// TODO
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Button, Divider, Select, SelectProps, Tooltip } from 'antd';
import NoContentFound from 'components/widgets/NoContentFound';
import { DefaultOptionType } from 'antd/es/select';

const defaultProps: SelectProps = {
	showSearch: true,
	allowClear: true,
	mode: 'multiple',
	optionFilterProp: 'label',
};
export interface IProps extends Omit<SelectProps, 'options'> {
	showSelectAll?: boolean;
	options: IMultiSelectOption[];
	value?: string[];
	shouldSortOptions?: boolean;
	onChange?: (value: string[]) => void;
}

const matchOption = (inputValue: string, option?: DefaultOptionType): boolean => {
	const lowered = _.trim(_.toLower(option?.title));
	const trimmed = _.trim(_.replace(_.toLower(option?.title), / /g, ''));
	const loweredSearch = _.trim(_.toLower(inputValue));
	const trimmedSearch = _.trim(_.replace(_.toLower(inputValue), / /g, ''));
	return (
		_.includes(lowered, loweredSearch) ||
		_.includes(lowered, trimmedSearch) ||
		_.includes(trimmed, loweredSearch) ||
		_.includes(trimmed, trimmedSearch)
	);
};

/**
 * filter options with label & value
 */
const filterMultiSelectOptions = (data: IMultiSelectOption[]): IMultiSelectOption[] => {
	return _.filter(data, (option) => !!option.label && !!option?.value);
};

/**
 * MultiSelect
 * @param {IProps} props
 */
const MultiSelect: React.FC<IProps> = ({
	value,
	options,
	showSelectAll = true,
	maxTagCount = 'responsive',
	shouldSortOptions = true,
	onChange,
	...restProps
}) => {
	const allProps = { ...defaultProps, ...restProps };
	const [selectedValue, setSelectedValue] = useState<string[]>();
	const [searchValue, setSearchValue] = useState<string>('');

	useEffect(() => {
		setSelectedValue(_.isEmpty(value) ? undefined : value);
	}, [value]);

	const allOptions = useMemo(
		() => _.flatMapDeep(options, (item) => (item.options ? item.options.map((option) => option) : item)),
		[options],
	);

	const isAllSelected = !_.isEmpty(options) && !_.isEmpty(selectedValue) && _.size(allOptions) === _.size(selectedValue);

	const getSelectOption = (option: IMultiSelectOption): React.ReactNode => {
		return (
			<Select.Option
				disabled={option.disabled}
				key={option.value}
				value={option.value}
				label={option.label}
				title={option?.title || option.label}
			>
				<span style={{ color: option.color }}>{option.label}</span>
			</Select.Option>
		);
	};
	const selectOptions = useMemo(() => {
		const sortedOptions = shouldSortOptions
			? _.sortBy(options, (option) => (_.includes(selectedValue, option.value) ? 1 : 2))
			: options;
		return _.map(sortedOptions, (item, index) => {
			if (!item.value && !_.isEmpty(item?.options)) {
				return (
					<Select.OptGroup key={index} label={item.label}>
						{_.map(item.options, (option) => getSelectOption(option))}
					</Select.OptGroup>
				);
			}
			return getSelectOption(item);
		});
	}, [options, selectedValue, shouldSortOptions]);

	const selectAll = () => {
		const validValues = _.chain(allOptions)
			.filter((option) => !option.disabled && _.includes(_.toLower(option.label), _.toLower(searchValue)))
			.map('value')
			.value() as string[];
		const filteredValues = _.uniq(_.concat(validValues, selectedValue || []));

		setSelectedValue(isAllSelected ? undefined : filteredValues);
		setSearchValue('');
		if (onChange && _.isFunction(onChange)) {
			onChange(isAllSelected ? [] : filteredValues);
		}
	};
	const onSelectionChange = (newValue: string[]) => {
		setSelectedValue(newValue);
		setSearchValue('');
		if (onChange && _.isFunction(onChange)) {
			onChange(newValue);
		}
	};

	return (
		<Select
			{...allProps}
			// todo css for classes
			className={`${restProps.className}`}
			value={selectedValue}
			maxTagCount={maxTagCount}
			onChange={onSelectionChange}
			onSearch={(searchTerm) => {
				setSearchValue(searchTerm);
			}}
			onOpenChange={() => setSearchValue('')}
			notFoundContent={<NoContentFound />}
			popupRender={(menu) => (
				<>
					{showSelectAll && (
						<>
							<Button className='select-all-button' type='text' onMouseDown={(e) => e.preventDefault()} onClick={selectAll}>
								{isAllSelected ? 'Deselect All' : 'Select All'}
							</Button>
							<Divider style={{ margin: 2 }} />
						</>
					)}
					{menu}
				</>
			)}
			maxTagPlaceholder={(values) => (
				<Tooltip
					title={
						<div>
							{_.map(values, (item) => (
								<div key={item?.value}>{item?.title || item?.label}</div>
							))}
						</div>
					}
				>
					+{_.size(values)}
				</Tooltip>
			)}
		>
			{selectOptions}
		</Select>
	);
};

export { filterMultiSelectOptions, matchOption };
export default MultiSelect;
